import type { InsightsChannel } from "@hightouch/lib/ad-studio/insights-channels/registry";
import type { AdFacts } from "src/pages/ad-studio/insights/ad-action-prompt";
import { formatOf } from "src/pages/ad-studio/insights/ad-medium";
import {
  availableColumns,
  fatigueBadge,
  initialSegmentsOf,
  metricOf,
  statusBadge,
} from "src/pages/ad-studio/insights/performance/utils/meta";
import { toRetargetedFilters } from "src/pages/ad-studio/insights/performance/filters/utils";
import { getEntityLabel } from "src/pages/ad-studio/insights/shared/channels";
import { formatMetricValue } from "src/pages/ad-studio/insights/shared/format";
import { mapQueryResult } from "src/pages/ad-studio/insights/performance/utils/query-result";
import type {
  AdDrilldownFailure,
  AdDrilldownState,
  AdHeader,
  AdHeaderExtras,
  AdMedia,
  AdViewMeta,
  CubeQueryFailure,
  DailySeries,
  Found,
  InsightsFailure,
  InsightsQueryResult,
  ListedAd,
  DetailedAdRow,
  ChannelScope,
  ChildList,
  DrillScope,
  SuggestedActionsFlag,
  SuggestedActionsOffer,
  InsightsMeta,
  EntityHeader,
  EntityStatus,
  AdHeaderRow,
  MetricCell,
  FoundAd,
  ParentHeaderRow,
  MetricColumn,
  TabList,
  ViewMeta,
} from "src/pages/ad-studio/insights/performance/types";
import type { CellBadge } from "src/pages/ad-studio/insights/performance/view/types";

export const channelOf = (drill: DrillScope): InsightsChannel =>
  drill.query.scope[0].channel;

const drillSyncIdOf = ({
  query: {
    scope: [
      {
        syncs: [sync],
      },
    ],
  },
}: DrillScope): string => sync.syncId;

const toTiles = (tiles: readonly MetricCell[]): EntityHeader["tiles"] =>
  tiles.flatMap(({ column, value }) =>
    column.whenEmpty === "show-dash" || value !== "not-reported"
      ? [
          {
            key: column.key,
            label: column.label,
            value: formatMetricValue(value, column.format),
          },
        ]
      : [],
  );

const toPromptMetrics = (tiles: readonly MetricCell[]): AdFacts["metrics"] =>
  tiles.flatMap(({ column, value }) =>
    value === "not-reported"
      ? []
      : [
          {
            label: column.label,
            value: formatMetricValue(value, column.format),
          },
        ],
  );

const statusBadges = ({
  status,
  view,
}: {
  status: EntityStatus;
  view: ViewMeta;
}): readonly CellBadge[] => {
  switch (status.kind) {
    case "status": {
      return [statusBadge({ view, value: status.value })];
    }
    case "no-status": {
      return [];
    }
    default: {
      return status satisfies never;
    }
  }
};

const healthBadge = ({
  row,
  view,
}: {
  row: DetailedAdRow;
  view: AdViewMeta;
}): CellBadge => {
  switch (row.fatigue.kind) {
    case "no-fatigue-row": {
      return { label: "Not reported", variant: "subtle" };
    }
    case "reported": {
      return fatigueBadge({ view, value: row.fatigue.status });
    }
    default: {
      return row.fatigue satisfies never;
    }
  }
};

const valueLabel = ({
  view,
  member,
  value,
}: {
  view: AdViewMeta;
  member: string;
  value: string;
}): string => view.valueLabels.get(member)?.get(value) ?? value;

type ReportedFatigue = Extract<DetailedAdRow["fatigue"], { kind: "reported" }>;

const toReportedPromptFatigue = ({
  view,
  reported,
}: {
  view: AdViewMeta;
  reported: ReportedFatigue;
}): AdFacts["fatigue"] => {
  const { fatigue } = view.layouts;
  const { peakCtr, cause } = reported;
  return {
    ...reported,
    statusLabel: fatigueBadge({ view, value: reported.status }).label,
    peakCtr:
      peakCtr === "not-reported"
        ? { kind: "not-found" }
        : {
            kind: "found",
            value: formatMetricValue(
              peakCtr,
              metricOf(view, fatigue.ctr).format,
            ),
          },
    cause:
      cause.kind === "cause"
        ? {
            kind: "found",
            value: valueLabel({
              view,
              member: fatigue.cause,
              value: cause.text,
            }),
          }
        : { kind: "not-found" },
  };
};

const toPromptFatigue = ({
  view,
  row,
}: {
  view: AdViewMeta;
  row: DetailedAdRow;
}): AdFacts["fatigue"] => {
  switch (row.fatigue.kind) {
    case "no-fatigue-row": {
      return "no-fatigue-row";
    }
    case "reported": {
      return toReportedPromptFatigue({ view, reported: row.fatigue });
    }
    default: {
      return row.fatigue satisfies never;
    }
  }
};

const toAdFacts = ({
  view,
  row,
  extras,
  tags,
}: {
  view: AdViewMeta;
  row: DetailedAdRow;
  extras: AdHeaderExtras;
  tags: readonly string[];
}): AdFacts => ({
  entityId: row.entityId,
  entityName: row.entityName,
  campaignName: extras.campaignName,
  adSetName: extras.adSetName,
  status: row.status,
  creativeCopy: extras.creativeCopy,
  tags,
  metrics: toPromptMetrics(row.tiles),
  fatigue: toPromptFatigue({ view, row }),
});

const fatigueNotes = (row: DetailedAdRow): readonly string[] => {
  switch (row.fatigue.kind) {
    case "no-fatigue-row": {
      return [];
    }
    case "reported": {
      return reportedFatigueNotes(row.fatigue);
    }
    default: {
      return row.fatigue satisfies never;
    }
  }
};

const reportedFatigueNotes = ({
  vsPeakPct,
  daysActive,
  cause,
}: ReportedFatigue): readonly string[] => [
  ...(vsPeakPct === "not-reported" ? [] : [`${vsPeakPct.toFixed(0)}% vs peak`]),
  `${daysActive}d active`,
  ...(cause.kind === "cause" ? [`(${cause.text})`] : []),
];

const fatiguingOf = ({
  row: { fatigue },
  view,
}: {
  row: DetailedAdRow;
  view: AdViewMeta;
}): "fatiguing" | "not-fatiguing" => {
  switch (fatigue.kind) {
    case "no-fatigue-row": {
      return "not-fatiguing";
    }
    case "reported": {
      return view.layouts.fatigue.fatiguing.includes(fatigue.status)
        ? "fatiguing"
        : "not-fatiguing";
    }
    default: {
      return fatigue satisfies never;
    }
  }
};

const capitalized = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

const toAdHeader = ({
  row,
  adSetId,
  view,
}: {
  row: DetailedAdRow;
  adSetId: Found<string>;
  view: AdViewMeta;
}): AdHeader => ({
  title: row.entityName,
  tags: [
    ...mediaTypeTags(row.media),
    ...(adSetId.kind === "found" ? [`Ad set: ${adSetId.value}`] : []),
    ...(row.objective.kind === "objective" ? [row.objective.text] : []),
  ],
  statuses: [
    ...statusBadges({ status: row.status, view }),
    healthBadge({ row, view }),
  ],
  notes: fatigueNotes(row),
});

const mediaTypeTags = (media: AdMedia): readonly string[] => {
  const format = formatOf(media);
  return format === "no-media" ? [] : [capitalized(format)];
};

const toListedHeader = ({
  row,
  view,
}: {
  row: ListedAd;
  view: AdViewMeta;
}): AdHeader => ({
  title: row.entityName,
  tags: mediaTypeTags(row.media),
  statuses: statusBadges({ status: row.status, view }),
  notes: [],
});

export const toFoundAd = ({
  header,
  tags,
  drill,
}: {
  header: AdHeaderRow;
  tags: readonly string[];
  drill: DrillScope;
}): FoundAd => {
  const { row, extras } = header;
  const view = drill.meta.views.ad;
  return {
    header: {
      ...toAdHeader({ row, adSetId: extras.adSetId, view }),
      tiles: toTiles(row.tiles),
    },
    ad: toAdFacts({ view, row, extras, tags }),
    fatigue: fatiguingOf({ row, view }),
  };
};

const toDetailRow = ({
  detail,
  heading,
}: {
  detail: InsightsQueryResult<Found<AdHeaderRow>, CubeQueryFailure>;
  heading: string;
}): InsightsQueryResult<AdHeaderRow, AdDrilldownFailure> => {
  switch (detail.status) {
    case "loading":
    case "failed": {
      return detail;
    }
    case "loaded": {
      const found = detail.value;
      switch (found.kind) {
        case "found": {
          return { status: "loaded", value: found.value };
        }
        case "not-found": {
          return {
            status: "failed",
            failure: { cause: "ad-not-found", heading },
          };
        }
        default: {
          return found satisfies never;
        }
      }
    }
    default: {
      return detail satisfies never;
    }
  }
};

const fromListedOrDetail = <T>({
  listed,
  detail,
  fromListed,
  fromDetail,
}: {
  listed: ListedAd | "no-list-row";
  detail: InsightsQueryResult<AdHeaderRow, AdDrilldownFailure>;
  fromListed: (row: ListedAd) => T;
  fromDetail: (header: AdHeaderRow) => T;
}): InsightsQueryResult<T, AdDrilldownFailure> => {
  switch (detail.status) {
    case "loaded": {
      return { status: "loaded", value: fromDetail(detail.value) };
    }
    case "loading":
    case "failed": {
      return listed === "no-list-row"
        ? detail
        : { status: "loaded", value: fromListed(listed) };
    }
    default: {
      return detail satisfies never;
    }
  }
};

const toFoundAdResult = ({
  detail,
  tags,
  drill,
}: {
  detail: InsightsQueryResult<AdHeaderRow, AdDrilldownFailure>;
  tags: InsightsQueryResult<readonly string[], CubeQueryFailure>;
  drill: DrillScope;
}): InsightsQueryResult<FoundAd, AdDrilldownFailure> => {
  switch (tags.status) {
    case "loading":
    case "failed": {
      return tags;
    }
    case "loaded": {
      const value = tags.value;
      return mapQueryResult(detail, (header) =>
        toFoundAd({ header, tags: value, drill }),
      );
    }
    default: {
      return tags satisfies never;
    }
  }
};

/** Each region of an ad's drilldown: header and media come from the opened list row until the detail query answers. */
export const adDrilldownStateOf = ({
  drill,
  adId,
  listed,
  detail,
  tags,
  daily,
  suggestedActions,
}: {
  drill: DrillScope;
  adId: string;
  listed: ListedAd | "no-list-row";
  detail: InsightsQueryResult<Found<AdHeaderRow>, CubeQueryFailure>;
  tags: InsightsQueryResult<readonly string[], CubeQueryFailure>;
  daily: InsightsQueryResult<DailySeries, InsightsFailure>;
  suggestedActions: SuggestedActionsFlag;
}): AdDrilldownState => {
  const view = drill.meta.views.ad;
  const detailRow = toDetailRow({
    detail,
    heading: getEntityLabel(channelOf(drill), "ad"),
  });
  return {
    header: fromListedOrDetail({
      listed,
      detail: detailRow,
      fromListed: (row) => toListedHeader({ row, view }),
      fromDetail: ({ row, extras }) =>
        toAdHeader({ row, adSetId: extras.adSetId, view }),
    }),
    media: fromListedOrDetail({
      listed,
      detail: detailRow,
      fromListed: (row) => ({ media: row.media, title: row.entityName }),
      fromDetail: ({ row }) => ({ media: row.media, title: row.entityName }),
    }),
    metrics: mapQueryResult(detailRow, ({ row }) => toTiles(row.tiles)),
    daily,
    tags: { syncId: drillSyncIdOf(drill), adId },
    ad: toFoundAdResult({ detail: detailRow, tags, drill }),
    suggestedActions: toSuggestedActionsOffer({
      flag: suggestedActions,
      drill,
    }),
  };
};

const toSuggestedActionsOffer = ({
  flag,
  drill,
}: {
  flag: SuggestedActionsFlag;
  drill: DrillScope;
}): SuggestedActionsOffer => {
  switch (flag) {
    case "flag-off": {
      return "flag-off";
    }
    case "flag-on": {
      return channelOf(drill).hasSuggestedActions
        ? "offered"
        : "channel-without-actions";
    }
    default: {
      return flag satisfies never;
    }
  }
};

export function toEntityHeader({
  header,
  drill,
}: {
  header: ParentHeaderRow;
  drill: DrillScope;
}): EntityHeader {
  switch (header.grain) {
    case "ad_set": {
      const { row, campaignId } = header;
      return {
        title: row.entityName,
        tags: [
          getEntityLabel(channelOf(drill), "ad_set"),
          ...(campaignId.kind === "found"
            ? [`Campaign: ${campaignId.value}`]
            : []),
        ],
        statuses: [],
        notes: [],
        tiles: toTiles(row.tiles),
      };
    }
    case "campaign": {
      const { row } = header;
      const view = drill.meta.views.campaign;
      return {
        title: row.entityName,
        tags: [
          getEntityLabel(channelOf(drill), "campaign"),
          ...(row.objective.kind === "objective" ? [row.objective.text] : []),
        ],
        statuses: statusBadges({ status: row.status, view }),
        notes: [],
        tiles: toTiles(row.tiles),
      };
    }
    default: {
      return header satisfies never;
    }
  }
}

const offeredMetrics = ({
  scope,
  view,
}: {
  scope: readonly ChannelScope[];
  view: ViewMeta;
}): readonly MetricColumn[] => {
  const offered = new Set(
    availableColumns({ view, scope }).map((column) => column.key),
  );
  return view.metrics.filter((column) => offered.has(column.key));
};

const toTabChildList = ({
  meta,
  scope,
  view,
  list,
}: {
  meta: InsightsMeta;
  scope: readonly ChannelScope[];
  view: ViewMeta;
  list: TabList;
}): ChildList => {
  const from = Object.values(meta.views).find(
    (candidate) => candidate.name === list.view,
  );
  const offered = offeredMetrics({ scope, view });
  return {
    segments: list.segments.filter((name) =>
      view.segments.some((segment) => segment.name === name),
    ),
    filters:
      from === undefined
        ? []
        : toRetargetedFilters({ filters: list.filters, from, to: view }),
    metrics: list.metrics.flatMap((key) =>
      offered.filter((column) => column.key === key),
    ),
  };
};

/** The list a drilldown opens on: the opening tab's filters, segments and metrics carried into `view`, or the view's defaults. */
export const toScopedList = ({
  meta,
  scope,
  list,
  view,
}: {
  meta: InsightsMeta;
  scope: readonly ChannelScope[];
  list: DrillScope["list"];
  view: ViewMeta;
}): ChildList =>
  list === "view-defaults"
    ? {
        segments: initialSegmentsOf(view),
        filters: [],
        metrics: offeredMetrics({ scope, view }).filter((column) =>
          column.place.includes("default-metric"),
        ),
      }
    : toTabChildList({ meta, scope, view, list });

export const toChildList = ({
  drill,
  view,
}: {
  drill: DrillScope;
  view: ViewMeta;
}): ChildList =>
  toScopedList({
    meta: drill.meta,
    scope: drill.query.scope,
    list: drill.list,
    view,
  });
